'use client';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

type Props = {
  children: React.ReactNode;
};

export function SmoothStickyHeader({ children }: Props) {
  const isDesktop = useBreakpoint('lg');
  const isTablet = useBreakpoint('md');
  const [isMounted, setIsMounted] = useState(false);

  const offsetPx = isMounted ? (isDesktop ? 32 : isTablet ? 16 : 8) : 8;

  const raf = useRef<number | null>(null);
  const [y, setY] = useState(offsetPx);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const clamp = (v: number) => Math.max(0, Math.min(offsetPx, v));

    const update = () => {
      raf.current = null;
      const scrolled = window.scrollY || 0;
      setY(clamp(offsetPx - scrolled));
    };

    const onScroll = () => {
      if (raf.current != null) return;
      raf.current = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf.current != null) window.cancelAnimationFrame(raf.current);
    };
  }, [offsetPx]);

  const isStuck = y === 0;

  return (
    <header
      className={cn(
        'custom-section fixed left-0 top-0 z-30 w-full transition-[padding] duration-75',
        isStuck ? '!px-0 sm:!px-0' : '!px-2 sm:!px-2',
      )}
      style={{
        transform: `translateY(${y}px)`,
        willChange: 'transform',
      }}
    >
      {children}
    </header>
  );
}
